// @flow

import { getTranslate } from 'react-localize-redux';

// Used when translations are not available (e.g. store not initialised yet).
// Must match the Danish texts in src/locale/session.locale.json (checked in authError.helper.test.js)
const FALLBACK_MESSAGES = {
  'session.error.network': 'Vi kunne ikke få forbindelse til serveren. Tjek din internetforbindelse og prøv igen.',
  'session.error.generic': 'Noget gik galt. Prøv igen om lidt.',
  'session.error.password_not_saved': 'Vi kunne ikke gemme din nye adgangskode. Prøv igen.',
  'session.unlock.error_link_expired_or_used': 'Linket er udløbet eller allerede brugt. Indtast din e-mail nedenfor, så sender vi dig et nyt.'
};

const authErrorHelper = {

  // Build translate function from redux thunk getState, returns null if locale is not ready
  translateFromState: (getState: any) => {
    try {
      return getTranslate(getState().locale);
    } catch (e) {
      return null;
    }
  },

  // Translate key, but never throw and always return a plain string
  translate: (translate: any, key: string) => {
    try {
      const value = typeof translate === 'function' ? translate(key) : null;
      if (typeof value === 'string' && value !== '' && value.indexOf('Missing translation') !== 0) {
        return value;
      }
    } catch (e) {
      // Ignore and use fallback below
    }
    return FALLBACK_MESSAGES[key] || FALLBACK_MESSAGES['session.error.generic'];
  },

  // Collect all non-empty strings from an array
  stringsFromArray: (value: any) => {
    if (!Array.isArray(value)) return [];
    return value.filter(item => typeof item === 'string' && item !== '');
  },

  // Collect all strings from array values of an object, e.g. { password: ['is too short'] }
  stringsFromObject: (value: any) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    let messages = [];
    Object.keys(value).forEach(key => {
      messages = messages.concat(authErrorHelper.stringsFromArray(value[key]));
    });
    return messages;
  },

  // Extract error messages from API response body. Supported shapes:
  //  - { errors: ['msg'] }                          (devise_token_auth)
  //  - { errors: { full_messages: ['msg'], ... } }  (devise_token_auth registration / password)
  //  - { errors: { email: ['msg'] } }               (devise_token_auth hash without full_messages)
  //  - { unlock_token: ['is invalid'] }             (plain Devise, errors at top level)
  messagesFromData: (data: any) => {
    if (!data || typeof data !== 'object') return [];
    const { errors } = data;

    if (Array.isArray(errors)) {
      return authErrorHelper.stringsFromArray(errors);
    }
    if (typeof errors === 'string' && errors !== '') {
      return [errors];
    }
    if (errors && typeof errors === 'object') {
      const fullMessages = authErrorHelper.stringsFromArray(errors.full_messages);
      return fullMessages.length ? fullMessages : authErrorHelper.stringsFromObject(errors);
    }
    return authErrorHelper.stringsFromObject(data);
  },

  // Turn axios error into message string, never throws.
  // clientErrorKey is used instead of the generic message when a 4xx response has no readable errors
  message: (error: any, translate: any, clientErrorKey: string = '', separator: string = '<hr />') => {
    try {
      if (!error || !error.response) {
        // Only request errors (no answer from server) are connection problems,
        // other exceptions (e.g. unexpected success body) get the generic message
        return authErrorHelper.isNetworkError(error)
          ? authErrorHelper.translate(translate, 'session.error.network')
          : authErrorHelper.translate(translate, 'session.error.generic');
      }

      const messages = authErrorHelper.messagesFromData(error.response.data);
      if (messages.length) {
        return messages.join(separator);
      }
      if (clientErrorKey && authErrorHelper.isClientError(error)) {
        return authErrorHelper.translate(translate, clientErrorKey);
      }
    } catch (e) {
      // Ignore and use fallback below
    }
    return authErrorHelper.translate(translate, 'session.error.generic');
  },

  // Check if request was sent but server did not answer (offline, timeout, CORS)
  isNetworkError: (error: any) => {
    if (!error || error.response) return false;
    return Boolean(error.request) || error.message === 'Network Error' || error.code === 'ECONNABORTED';
  },

  // Check if server answered with 4xx status
  isClientError: (error: any) => {
    const status = error && error.response ? Number(error.response.status) : 0;
    return status >= 400 && status < 500;
  }
}

export default authErrorHelper;



// WEBPACK FOOTER //
// ./src/helpers/authError.helper.js